import React, { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Collapse,
    Alert,
    Pagination,
    Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExpandMore, ExpandLess, Info } from '@mui/icons-material';
import { getFirestore, collection, query, orderBy, limit, getDocs, where, startAfter } from 'firebase/firestore';

const firestore = getFirestore();

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    '&.MuiTableCell-head': {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    '&.MuiTableCell-body': {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

const tipoOperacionColors = {
    'TRANSFERENCIA': { color: '#1976d2', bg: '#e3f2fd' },
    'ELIMINACION_ORDEN': { color: '#f57c00', bg: '#fff3e0' },
    'USO_MATERIALES': { color: '#388e3c', bg: '#e8f5e8' },
    'ACTUALIZACION_DIRECTA': { color: '#7b1fa2', bg: '#f3e5f5' }
};

const REGISTROS_POR_PAGINA = 50;

function ListRegistros() {
    const [registros, setRegistros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [filtros, setFiltros] = useState({
        tipoOperacion: '',
        idCuadrilla: '',
        fechaDesde: '',
        fechaHasta: ''
    });
    const [pagina, setPagina] = useState(1);
    const [totalPaginas, setTotalPaginas] = useState(1);
    const [ultimoDoc, setUltimoDoc] = useState(null);

    useEffect(() => {
        fetchRegistros();
    }, [pagina, filtros]);

    const fetchRegistros = async () => {
        setLoading(true);
        try {
            let consultaBase = collection(firestore, 'registros');
            const condiciones = [];

            // Aplicar filtros
            if (filtros.tipoOperacion) {
                condiciones.push(where('tipoOperacion', '==', filtros.tipoOperacion));
            }
            if (filtros.idCuadrilla) {
                condiciones.push(where('idCuadrilla', '==', filtros.idCuadrilla));
            }

            // Construir consulta
            const consulta = query(
                consultaBase,
                ...condiciones,
                orderBy('timestamp', 'desc'),
                limit(REGISTROS_POR_PAGINA)
            );

            const snapshot = await getDocs(consulta);
            const registrosData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setRegistros(registrosData);
            setUltimoDoc(snapshot.docs[snapshot.docs.length - 1]);
            
            // Calcular total de páginas (aproximado)
            setTotalPaginas(Math.ceil(snapshot.size / REGISTROS_POR_PAGINA));
        } catch (error) {
            console.error('Error al obtener registros:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExpandRow = (registroId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(registroId)) {
            newExpandedRows.delete(registroId);
        } else {
            newExpandedRows.add(registroId);
        }
        setExpandedRows(newExpandedRows);
    };

    const formatearFecha = (timestamp) => {
        if (!timestamp) return 'N/A';
        const fecha = new Date(timestamp);
        return fecha.toLocaleString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getTipoOperacionChip = (tipo) => {
        const config = tipoOperacionColors[tipo] || { color: '#666', bg: '#f5f5f5' };
        return (
            <Chip
                label={tipo}
                size="small"
                style={{
                    backgroundColor: config.bg,
                    color: config.color,
                    fontWeight: 'bold'
                }}
            />
        );
    };

    const renderSerialesDetalle = (seriales, tipo) => {
        if (!seriales || seriales.length === 0) return null;

        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" color={tipo === 'agregados' ? 'success.main' : 'error.main'}>
                    {tipo === 'agregados' ? 'Seriales Agregados' : 'Seriales Removidos'} ({seriales.length})
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {seriales.slice(0, 10).map((serial, index) => (
                        <Chip
                            key={index}
                            label={serial.id || serial}
                            size="small"
                            variant="outlined"
                            color={tipo === 'agregados' ? 'success' : 'error'}
                        />
                    ))}
                    {seriales.length > 10 && (
                        <Chip
                            label={`+${seriales.length - 10} más`}
                            size="small"
                            variant="outlined"
                            color="default"
                        />
                    )}
                </Box>
            </Box>
        );
    };

    const renderMetadatos = (metadatos) => {
        if (!metadatos || Object.keys(metadatos).length === 0) return null;

        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" color="textSecondary">
                    Metadatos:
                </Typography>
                <Box sx={{ pl: 2 }}>
                    {Object.entries(metadatos).map(([key, value]) => (
                        <Typography key={key} variant="body2" color="textSecondary">
                            <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </Typography>
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Registros de Seriales
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Este registro muestra todas las operaciones que afectan seriales en las cuadrillas.
                Use los filtros para encontrar operaciones específicas.
            </Alert>

            {/* Filtros */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Tipo de Operación</InputLabel>
                    <Select
                        value={filtros.tipoOperacion}
                        label="Tipo de Operación"
                        onChange={(e) => setFiltros({ ...filtros, tipoOperacion: e.target.value })}
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="TRANSFERENCIA">Transferencia</MenuItem>
                        <MenuItem value="ELIMINACION_ORDEN">Eliminación Orden</MenuItem>
                        <MenuItem value="USO_MATERIALES">Uso Materiales</MenuItem>
                        <MenuItem value="ACTUALIZACION_DIRECTA">Actualización Directa</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    label="ID Cuadrilla"
                    value={filtros.idCuadrilla}
                    onChange={(e) => setFiltros({ ...filtros, idCuadrilla: e.target.value })}
                    sx={{ minWidth: 120 }}
                />

                <TextField
                    size="small"
                    label="Fecha Desde"
                    type="date"
                    value={filtros.fechaDesde}
                    onChange={(e) => setFiltros({ ...filtros, fechaDesde: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    size="small"
                    label="Fecha Hasta"
                    type="date"
                    value={filtros.fechaHasta}
                    onChange={(e) => setFiltros({ ...filtros, fechaHasta: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>

            {/* Tabla de registros */}
            <TableContainer component={Paper}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell width="50px">Detalles</StyledTableCell>
                            <StyledTableCell>Fecha/Hora</StyledTableCell>
                            <StyledTableCell>Tipo Operación</StyledTableCell>
                            <StyledTableCell>ID Cuadrilla</StyledTableCell>
                            <StyledTableCell>ID Relacionado</StyledTableCell>
                            <StyledTableCell align="center">Agregados</StyledTableCell>
                            <StyledTableCell align="center">Removidos</StyledTableCell>
                            <StyledTableCell>Usuario</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {registros.map((registro) => (
                            <React.Fragment key={registro.id}>
                                <StyledTableRow>
                                    <StyledTableCell>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleExpandRow(registro.id)}
                                        >
                                            {expandedRows.has(registro.id) ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {formatearFecha(registro.timestamp)}
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {getTipoOperacionChip(registro.tipoOperacion)}
                                    </StyledTableCell>
                                    <StyledTableCell>{registro.idCuadrilla}</StyledTableCell>
                                    <StyledTableCell>{registro.idRelacionado}</StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Chip
                                            label={registro.cantidadAgregados}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <Chip
                                            label={registro.cantidadRemovidos}
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                        />
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        {registro.usuario || 'Sistema'}
                                    </StyledTableCell>
                                </StyledTableRow>
                                
                                {/* Fila expandida con detalles */}
                                <StyledTableRow>
                                    <StyledTableCell colSpan={8} sx={{ py: 0 }}>
                                        <Collapse in={expandedRows.has(registro.id)} timeout="auto" unmountOnExit>
                                            <Box sx={{ margin: 1 }}>
                                                {renderSerialesDetalle(registro.serialesAgregados, 'agregados')}
                                                {renderSerialesDetalle(registro.serialesRemovidos, 'removidos')}
                                                {renderMetadatos(registro.metadatos)}
                                            </Box>
                                        </Collapse>
                                    </StyledTableCell>
                                </StyledTableRow>
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Paginación */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                    count={totalPaginas}
                    page={pagina}
                    onChange={(event, value) => setPagina(value)}
                    color="primary"
                />
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Typography>Cargando registros...</Typography>
                </Box>
            )}
        </Box>
    );
}

export default ListRegistros; 